import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Share2, UserPlus } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DataConnection } from "@/api/data-connections-api"
import type { DirectorySearchResult } from "@/services/directory-search-service"
import type { ParticipantRole } from "@/services/project-service"

import {
  addDataConnectionParticipant,
  listDataConnectionParticipants,
  removeDataConnectionParticipant,
  updateDataConnectionParticipantRole,
} from "@/api/data-connections-api"
import { DirectorySearch } from "@/components/directory-search"
import { DATA_CONNECTIONS_KEY } from "@/features/admin-dashboard/hooks/use-data-connections"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { ParticipantList } from "@/features/projects/components/participant-list"

type Props = {
  connection: DataConnection | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Fas 1 of connection sharing only exposes the `reader` role — it means
// "can use this connection to build dashboards". Editor/admin levels on
// a connection are deferred; global admin (admin_dashboard group) keeps
// being the management surface.
const PARTICIPANT_ROLE: ParticipantRole = "reader"

export function DataConnectionShareDialog({ connection, open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const connectionId = connection?.id

  const participantsQueryKey = ["data-connection-participants", connectionId ?? ""] as const
  const { data: participants = [], isLoading } = useQuery({
    queryKey: participantsQueryKey,
    queryFn: () => listDataConnectionParticipants(connectionId!),
    enabled: !!connectionId && open,
    // staleTime 0 — always refetch when the dialog opens so the access
    // list reflects the current server state, never a stale cache.
    staleTime: 0,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: participantsQueryKey })
    // Refresh the connections list too so the row's "shared with N"
    // indicator updates without a manual reload.
    void queryClient.invalidateQueries({ queryKey: DATA_CONNECTIONS_KEY })
  }

  const addParticipant = useMutation({
    mutationFn: (result: DirectorySearchResult) =>
      addDataConnectionParticipant(connectionId!, {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: PARTICIPANT_ROLE,
      }),
    onSuccess: () => {
      void invalidate()
      toast.success(
        t("adminDashboard.dataConnections.sharing.add_success", {
          defaultValue: "Access granted",
        })
      )
    },
    onError: (err: Error) => {
      toast.error(
        t("adminDashboard.dataConnections.sharing.add_error", {
          defaultValue: "Failed to grant access",
        }),
        { description: err.message }
      )
    },
  })

  const updateRole = useMutation({
    mutationFn: ({ oid, role }: { oid: string; role: ParticipantRole }) =>
      updateDataConnectionParticipantRole(connectionId!, oid, role),
    onSuccess: invalidate,
    onError: (err: Error) => {
      toast.error(
        t("adminDashboard.dataConnections.sharing.role_error", {
          defaultValue: "Failed to update access level",
        }),
        { description: err.message }
      )
    },
  })

  const removeParticipant = useMutation({
    mutationFn: (oid: string) => removeDataConnectionParticipant(connectionId!, oid),
    onSuccess: () => {
      void invalidate()
      toast.success(
        t("adminDashboard.dataConnections.sharing.remove_success", {
          defaultValue: "Access revoked",
        })
      )
    },
    onError: (err: Error) => {
      toast.error(
        t("adminDashboard.dataConnections.sharing.remove_error", {
          defaultValue: "Failed to revoke access",
        }),
        { description: err.message }
      )
    },
  })

  const existingOids = participants.map((p) => p.oid).filter(Boolean) as string[]
  // local state of a brief refresh tick so the title doesn't flicker
  const [titleTick] = useState(0)
  void titleTick

  const handleAdd = (result: DirectorySearchResult) => {
    if (!connectionId) return
    if (existingOids.includes(result.oid)) return
    addParticipant.mutate(result)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5" />
            {t("adminDashboard.dataConnections.sharing.dialog_title", {
              defaultValue: "Share data connection",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.dataConnections.sharing.dialog_description", {
              defaultValue:
                "Grant users or AAD groups permission to use {{name}} in their dashboards. Global admins always have access.",
              name: connection?.name ?? connection?.id ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <UserPlus className="text-muted-foreground size-4" />
              <Label className="text-sm font-medium">
                {t("adminDashboard.dataConnections.sharing.add_participant", {
                  defaultValue: "Add people",
                })}
              </Label>
            </div>

            <DirectorySearch
              disabled={addParticipant.isPending}
              selectedOids={existingOids}
              onSelect={handleAdd}
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t("adminDashboard.dataConnections.sharing.participants", {
                defaultValue: "People with access",
              })}
            </Label>
            <ParticipantList
              canManage
              // Connections only support `reader` in fas 1 — the role
              // dropdown defaults to the full reader/editor/admin set
              // it offers for project sharing, which would 400 against
              // the connection backend. Lock it down here.
              allowedRoles={["reader"]}
              entityType="project"
              isLoading={isLoading}
              participants={participants}
              emptyParticipantsText={t("adminDashboard.dataConnections.sharing.no_participants", {
                defaultValue: "No participants yet. Only global admins can use this connection.",
              })}
              onRemove={(oid) => removeParticipant.mutate(oid)}
              onRoleChange={(oid, role) => updateRole.mutate({ oid, role })}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
